import {memo, useCallback, useMemo} from 'react';

import * as Styled from './day-timeline.style';

import {dateFnsLocalizer} from 'react-big-calendar';
import {format, parse, startOfWeek, getDay} from 'date-fns';
import {enUS, ru} from 'date-fns/locale';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';

import {useTheme} from '@innowise-group/ui-kit';
import {LanguageService, useService} from '@innowise-group/core';
import {ProcessedMeetingRoomEvent} from '@shared/core';

import {BookingMeetingRoomFormFields} from '../booking-meeting-room-form';
import {
  checkEventsIsOverlapping,
  calculateEndTime,
  createInnerEvent,
  computeLimitEndTime,
  computeLimitStartTime,
} from '../../utilities';

const localesValue = {
  en: {'en-US': enUS},
  ru: {'ru-RU': ru},
};

const TIMELINE_STEP_MINUTES = 15;
const TIMELINE_TIMESLOTS = 4;

type FieldValuesType = Omit<ProcessedMeetingRoomEvent, 'isDraggable' | 'id'>;

interface DayTimelineProps {
  setValue: (fieldName: string, value: Date, options?: {shouldDirty?: boolean; shouldValidate?: boolean}) => void;
  value: FieldValuesType;
  selectedDate: Date;
  events: ProcessedMeetingRoomEvent[];
}

const DragAndDropCalendar = withDragAndDrop(Styled.Timeline);

const DayTimeline = ({setValue, value, selectedDate, events}: DayTimelineProps) => {
  const event = createInnerEvent(value.start, value.end);
  const theme = useTheme();
  const {getAppLanguage} = useService(LanguageService);

  const localizer = useMemo(() => {
    const locale = getAppLanguage();
    return dateFnsLocalizer({
      format,
      parse,
      startOfWeek,
      getDay,
      locales: localesValue[locale] || localesValue.ru,
    });
  }, [getAppLanguage]);

  const limitStartTime = useMemo(() => computeLimitStartTime(selectedDate ? selectedDate : new Date()), [selectedDate]);
  const limitEndTime = useMemo(() => computeLimitEndTime(selectedDate ? selectedDate : new Date()), [selectedDate]);

  const eventPropGetter = (event) => ({
    ...(event.isDraggable && {
      style: {
        backgroundColor: theme.palette.specials.meetings.backSecondary,
        left: '0',
        minWidth: '100%',
      },
    }),
  });

  const handleSelectSlot = useCallback(
    ({start, end}: {start: Date; end: Date}) => {
      const newEvent = createInnerEvent(start, end);

      const isOverlapping = checkEventsIsOverlapping(newEvent, events);
      if (isOverlapping) return;

      setValue(BookingMeetingRoomFormFields.StartTime, newEvent.start, {shouldValidate: true, shouldDirty: true});
      setValue(BookingMeetingRoomFormFields.EndTime, newEvent.end, {shouldValidate: true, shouldDirty: true});
    },
    [events, setValue],
  );

  const handleSelecting = useCallback(
    ({start, end}) => {
      const newEvent = createInnerEvent(start, end);
      const isOverlapping = checkEventsIsOverlapping(newEvent, events);
      return !isOverlapping;
    },
    [events],
  );

  const onEventChange = useCallback(
    (props) => {
      const {event, start, end} = props;
      if (start >= limitEndTime || start < limitStartTime) return;
      const modifiedEndTime = calculateEndTime(start, end);

      const updatedEvent = {...event, start, end: modifiedEndTime};

      const isOverlapping = checkEventsIsOverlapping(updatedEvent, events);
      if (isOverlapping) return;

      setValue(BookingMeetingRoomFormFields.StartTime, updatedEvent.start, {shouldValidate: true, shouldDirty: true});
      setValue(BookingMeetingRoomFormFields.EndTime, updatedEvent.end, {shouldValidate: true, shouldDirty: true});
    },
    [limitEndTime, limitStartTime, events, setValue],
  );

  return (
    <Styled.Wrapper>
      <DragAndDropCalendar
        views="day"
        selectable
        localizer={localizer}
        defaultDate={new Date()}
        date={selectedDate}
        onNavigate={() => {
          return selectedDate ? selectedDate : new Date();
        }}
        defaultView="day"
        events={[...events, event]}
        onSelectSlot={handleSelectSlot}
        onSelecting={handleSelecting}
        toolbar={false}
        scrollToTime={new Date().setHours(9, 0, 0, 0)}
        formats={{
          timeGutterFormat: (date) => localizer.format(date, 'HH:mm'),
          eventTimeRangeFormat: ({start, end}, culture, localizer) => {
            return localizer.format(start, 'HH:mm', culture) + ' - ' + localizer.format(end, 'HH:mm', culture);
          },
          selectRangeFormat: ({start, end}, culture, localizer) => {
            return localizer.format(start, 'HH:mm', culture) + ' - ' + localizer.format(end, 'HH:mm', culture);
          },
        }}
        step={TIMELINE_STEP_MINUTES}
        timeslots={TIMELINE_TIMESLOTS}
        BigCalendar
        overlapping={false}
        draggableAccessor="isDraggable"
        onEventResize={onEventChange}
        resizable
        onEventDrop={onEventChange}
        eventPropGetter={eventPropGetter}
        startAccessor="start"
        endAccessor="end"
        min={limitStartTime}
        max={limitEndTime}
      />
    </Styled.Wrapper>
  );
};

export default memo(DayTimeline);
